import { useMemo, useState } from "react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { CalendarDays, Thermometer, Droplets } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUnit } from "@/components/unit-provider";

interface DailyEntry {
  dt: number;
  temp: { max: number; min: number; day?: number };
  pop?: number;
  rain?: number;
  snow?: number;
  weather?: { icon: string; description: string }[];
}

interface WeatherCalendarProps {
  daily?: DailyEntry[];
  metric?: "temp" | "precip";
}

function tempColor(t: number): string {
  // Map -20..40°C to a color.
  if (t <= -10) return "bg-indigo-500/80";
  if (t <= 0) return "bg-sky-500/80";
  if (t <= 10) return "bg-cyan-500/70";
  if (t <= 18) return "bg-emerald-500/70";
  if (t <= 24) return "bg-lime-500/70";
  if (t <= 30) return "bg-amber-500/80";
  return "bg-rose-500/80";
}

function precipColor(p: number): string {
  // p = pop fraction 0..1
  if (p < 0.1) return "bg-slate-500/30";
  if (p < 0.3) return "bg-sky-500/30";
  if (p < 0.5) return "bg-sky-500/55";
  if (p < 0.75) return "bg-blue-500/75";
  return "bg-blue-600/95";
}

function dayLabel(ts: number) {
  return new Date(ts * 1000).toLocaleDateString([], { weekday: "short" });
}
function dateLabel(ts: number) {
  return new Date(ts * 1000).toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });
}

export function WeatherCalendar({ daily, metric: initial = "temp" }: WeatherCalendarProps) {
  const { display } = useUnit();
  const [metric, setMetric] = useState<"temp" | "precip">(initial);

  // Build an 8-day calendar (typical OWM daily payload).
  const days = useMemo(() => (daily || []).slice(0, 8), [daily]);

  if (days.length === 0) return null;

  // Pad start so first day lines up with weekday column.
  const first = new Date(days[0].dt * 1000);
  const firstDow = first.getDay();

  // Build a grid: leading blanks + days. Cap at 14 cells (2 weeks).
  const cells: (DailyEntry | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (const d of days) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <GlassPanel intensity="low" className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
            <CalendarDays className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight">Forecast Calendar</h3>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Next 8 days · heat-mapped
            </p>
          </div>
        </div>

        <div className="inline-flex items-center gap-1 rounded-full border border-border/50 bg-background/40 p-0.5">
          <button
            onClick={() => setMetric("temp")}
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors",
              metric === "temp"
                ? "bg-primary/15 text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Thermometer className="h-3 w-3" />
            Temperature
          </button>
          <button
            onClick={() => setMetric("precip")}
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors",
              metric === "precip"
                ? "bg-primary/15 text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Droplets className="h-3 w-3" />
            Precipitation
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5 mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div
            key={d}
            className="text-center text-[10px] uppercase tracking-wider text-muted-foreground font-semibold"
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((d, i) => {
          if (!d) return <div key={`b${i}`} className="aspect-square" />;
          const high = d.temp.max;
          const low = d.temp.min;
          const pop = d.pop ?? 0;
          const heatClass =
            metric === "temp" ? tempColor((high + low) / 2) : precipColor(pop);
          return (
            <div
              key={d.dt}
              className={cn(
                "aspect-square rounded-lg border border-border/40 relative overflow-hidden flex flex-col justify-between p-1.5 group/day cursor-help",
              )}
              title={`${dateLabel(d.dt)} · ${d.weather?.[0]?.description ?? ""} · ${display(low)} / ${display(high)} · ${Math.round(pop * 100)}% precip`}
            >
              <div className={cn("absolute inset-0 opacity-90", heatClass)} />
              <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/20 to-black/40" />
              <div className="relative z-10 flex justify-between items-start gap-1">
                <span className="text-[10px] font-bold text-white/95 tabular-nums">
                  {new Date(d.dt * 1000).getDate()}
                </span>
                {d.weather?.[0]?.icon && (
                  <img
                    src={`/api/icon/${d.weather[0].icon}.png`}
                    alt=""
                    className="h-5 w-5 -m-1 opacity-90"
                  />
                )}
              </div>
              <div className="relative z-10 flex flex-col">
                {metric === "temp" ? (
                  <p className="text-[10px] font-bold text-white tabular-nums leading-tight drop-shadow">
                    {Math.round(high)}°
                    <span className="text-white/70 font-normal">
                      {" "}/ {Math.round(low)}°
                    </span>
                  </p>
                ) : (
                  <p className="text-[10px] font-bold text-white tabular-nums leading-tight drop-shadow">
                    {Math.round(pop * 100)}%
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-between text-[10px] text-muted-foreground">
        {metric === "temp" ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span>cold</span>
            {[
              "bg-indigo-500/80",
              "bg-sky-500/80",
              "bg-cyan-500/70",
              "bg-emerald-500/70",
              "bg-lime-500/70",
              "bg-amber-500/80",
              "bg-rose-500/80",
            ].map((cls) => (
              <span key={cls} className={cn("h-2.5 w-3.5 rounded-sm", cls)} />
            ))}
            <span>hot</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <span>dry</span>
            {[
              "bg-slate-500/30",
              "bg-sky-500/30",
              "bg-sky-500/55",
              "bg-blue-500/75",
              "bg-blue-600/95",
            ].map((cls) => (
              <span key={cls} className={cn("h-2.5 w-3.5 rounded-sm", cls)} />
            ))}
            <span>wet</span>
          </div>
        )}
        <span>{daily && daily.length > 0 && dayLabel(daily[0].dt)} → {daily && daily.length > 0 && dateLabel(daily[Math.min(7, daily.length - 1)].dt)}</span>
      </div>
    </GlassPanel>
  );
}
